import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { authAPI } from '../services/api';
import { Button, Input, Card } from './ui';
import logoDefault from '../assets/almaquinta.svg';

/**
 * Pagina de "Olvide mi contraseña". Recibe email, dispara envio de correo con
 * enlace de reset. Respuesta uniforme (no revela si el email existe) — asi que
 * siempre mostramos el mensaje de exito.
 */
const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [enviado, setEnviado] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await authAPI.forgotPassword(email);
            setEnviado(true);
        } catch (err) {
            setError(err.message || 'No pudimos procesar la solicitud');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 bg-bg">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="hidden dark:block absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-primary/10 blur-3xl" />
                <div className="hidden dark:block absolute -bottom-40 -right-40 w-[480px] h-[480px] rounded-full bg-purple2/10 blur-3xl" />
            </div>

            <Card className="relative w-full max-w-md">
                <Card.Body className="p-8 sm:p-10">
                    <div className="flex flex-col items-center text-center mb-8">
                        <div className="w-20 h-20 rounded-2xl bg-white/95 p-2 shadow-soft mb-4 inline-flex items-center justify-center">
                            <img src={logoDefault} alt="Don Presupuesto" className="w-full h-full object-contain" />
                        </div>
                        <h1 className="text-2xl font-bold text-text">Recuperar contraseña</h1>
                        <p className="text-sm text-muted mt-1">
                            {enviado
                                ? 'Revisa tu correo para continuar'
                                : 'Ingresa tu email y te enviaremos un enlace'}
                        </p>
                    </div>

                    {enviado ? (
                        <div className="flex flex-col items-center gap-4">
                            <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center">
                                <CheckCircle2 size={28} className="text-success" />
                            </div>
                            <p className="text-sm text-center text-text">
                                Si <strong>{email}</strong> existe en el sistema, recibirás un enlace para restablecer tu contraseña.
                            </p>
                            <p className="text-xs text-muted text-center">
                                El enlace expira en 1 hora. Si no lo recibes, revisa la carpeta de spam.
                            </p>
                            <Link
                                to="/login"
                                className="mt-2 text-sm text-primary hover:underline inline-flex items-center gap-1"
                            >
                                <ArrowLeft size={14} /> Volver al login
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                            {error && (
                                <div className="px-3 py-2.5 rounded-lg bg-danger/10 border border-danger/30 text-danger text-sm">
                                    {error}
                                </div>
                            )}
                            <Input
                                type="email"
                                label="Email"
                                placeholder="tu@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoFocus
                                prefix={<Mail size={16} />}
                            />
                            <Button
                                type="submit"
                                variant="primary"
                                size="lg"
                                fullWidth
                                disabled={loading || !email.trim()}
                                className="mt-2"
                            >
                                {loading ? 'Enviando...' : 'Enviar enlace'}
                            </Button>
                            <Link
                                to="/login"
                                className="text-sm text-muted hover:text-text inline-flex items-center gap-1 justify-center mt-2"
                            >
                                <ArrowLeft size={14} /> Volver al login
                            </Link>
                        </form>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default ForgotPassword;
